import React, { useState } from "react";
import { formatDateTime } from "utils/time-utils";
import { TabPanel, TabView, TabViewTabChangeParams } from "primereact/tabview";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { TimeOptionKeys, TimeOptions } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { WeatherMonitoringProps } from "../charts/interfaces";
import { getStartEndTime, TabTypes } from "./WeatherMonitoringUtils";
import WeatherMonitoringTable from "./WeatherMonitoringTable";
import ChartPanel from "./ChartPanel";

const WeatherMonitoring = (props: WeatherMonitoringProps) => {
  const [timeInterval, setTimeInterval] = useState<TimeOptionKeys>(props.timeInterval as TimeOptionKeys);
  const [startTime, setStartTime] = useState<Date>(getStartEndTime(props.timeInterval as TimeOptionKeys, "start"));
  const [endTime, setEndTime] = useState<Date>(getStartEndTime(props.timeInterval as TimeOptionKeys, "end"));
  const [activeIndex, setActiveIndex] = useState<number>(
    props.timeInterval === TimeOptions.HOURS.value ? 0 : 1
  );
  const [activeIndexTable, setActiveIndexTable] = useState<number>(0);

  const isChartVisible = (): boolean | undefined => {
    return (
      props.temperature?.isVisible &&
      props.humidity?.isVisible &&
      props.precipitation?.isVisible &&
      props.windSpeed?.isVisible &&
      props.windDirection?.isVisible &&
      !!props.temperature.dataset?.identifier &&
      !!props.humidity.dataset?.identifier &&
      !!props.precipitation.dataset?.identifier &&
      !!props.windSpeed.dataset?.identifier &&
      !!props.windDirection.dataset?.identifier
    );
  };

  const datasetsIdentifiers = [
    props.temperature?.dataset?.identifier,
    props.feelsLikeTemperature?.dataset?.identifier,
    props.windSpeed?.dataset?.identifier,
    props.windDirection?.dataset?.identifier,
    props.humidity?.dataset?.identifier,
    props.precipitation?.dataset?.identifier,
    props.gusture?.dataset?.identifier,
  ].filter((identifier): identifier is string => identifier !== undefined);

  const { data: datasetsData, isLoading } = useGetDataInfoRange(
    datasetsIdentifiers,
    {
      timeStart: startTime.toISOString(),
      timeEnd: endTime.toISOString(),
    },
    {
      enabled: isChartVisible(),
    }
  );

  const handleTabChange = (e: TabViewTabChangeParams, tabsType: string) => {
    if (tabsType === TabTypes.TABS) {
      setActiveIndex(e.index);
      const currentTimeInterval = e.index === 0 ? "HOURS" : "DAYS";
      setTimeInterval(currentTimeInterval as TimeOptionKeys);
      setStartTime(getStartEndTime(currentTimeInterval as TimeOptionKeys, "start"));
      setEndTime(getStartEndTime(currentTimeInterval as TimeOptionKeys, "end"));
    }
    if (tabsType === TabTypes.SUBTABS) {
      setActiveIndexTable(e.index);
    }
  };

  const handleFormatHeaderString = (dayOffset: number) => {
    return formatDateTime(new Date(new Date().setDate(new Date().getDate() + dayOffset)), "MM-dd");
  };

  return (
    <>
      <div className="flex flex-column gap-2">
        <div className="font-semibold text-xl">{props.location}</div>
        {props.dateSelection?.isVisible && (
          <div className="flex flex-row gap-2">
            <CalendarWrapper
              id="from"
              value={startTime}
              onChange={setStartTime}
              className="p-mr-2"
              debounceTimeInMs={300}
            />
          </div>
        )}
      </div>
      {!isChartVisible() ? (
        <div className="text-center">
          To display chart please at least select these datasets: Temperature, Humidity,
          Precipitation, Wind Speed, Wind Direction
        </div>
      ) : (
        <TabView activeIndex={activeIndex} onTabChange={e => handleTabChange(e, TabTypes.TABS)}>
          <TabPanel header="Graphs Hourly">
            {datasetsData && (
              <ChartPanel
                datasetsData={datasetsData}
                currentChartData={props}
                timeInterval="HOURS"
                startTime={startTime}
                endTime={endTime}
                isChartVisible={!!isChartVisible()}
              />
            )}
          </TabPanel>
          <TabPanel header="Graphs Daily">
            {datasetsData && (
              <ChartPanel
                datasetsData={datasetsData}
                currentChartData={props}
                timeInterval="DAYS"
                startTime={startTime}
                endTime={endTime}
                isChartVisible={!!isChartVisible()}
              />
            )}
          </TabPanel>
          <TabPanel header="Table">
            <TabView
              activeIndex={activeIndexTable}
              onTabChange={e => handleTabChange(e, TabTypes.SUBTABS)}
            >
              {[0, 1, 2].map(dayOffset => (
                <TabPanel key={dayOffset} header={handleFormatHeaderString(dayOffset)}>
                  {timeInterval === "DAYS" && !isLoading && datasetsData && (
                    <WeatherMonitoringTable
                      datasetsData={datasetsData}
                      props={props}
                      dayOffset={dayOffset}
                      endHour={dayOffset === 2 ? new Date().getHours() : 23}
                      tabHeader={handleFormatHeaderString(dayOffset)}
                    />
                  )}
                </TabPanel>
              ))}
            </TabView>
          </TabPanel>
        </TabView>
      )}
    </>
  );
};

export default WeatherMonitoring;
